import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import { LearningFacadeService } from '../../../services/learning-facade.service';
import {
  SkillPracticeTaskResponse,
  SkillPracticeTaskStatus,
} from '../../../models/skill-learning.model';

const LEVEL_ONE = 1;
const POLLING_INTERVAL_MS = 60000;

@Component({
  selector: 'app-level1-task-card',
  templateUrl: './level1-task-card.component.html',
  styleUrls: ['./level1-task-card.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Level1TaskCardComponent implements OnInit, OnDestroy {
  @Input() specializationId: string;
  @Input() isDisabled: boolean = false;
  @Input() startHintKey: string = 'SKILL_LEARNING_SESSION_LEVEL1_TASK_START_HINT';
  @Input() lockedMessageKey: string = 'SKILL_LEARNING_SESSION_LEVEL1_TASK_LOCKED';

  @Output() completed = new EventEmitter<void>();

  taskStatus: SkillPracticeTaskStatus | null = null;
  curatorName: string | null = null;
  curatorPhone: string | null = null;
  planfixTaskId: number | null = null;
  planfixTaskUrl: string | null = null;
  isLoading: boolean = false;

  private destroy$ = new Subject<void>();
  private pollingInterval: any;

  constructor(
    private facade: LearningFacadeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadTaskStatus();
  }

  ngOnDestroy(): void {
    this.clearPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canStart(): boolean {
    return this.isRestartableStatus && !this.isDisabled && !!this.specializationId;
  }

  get canShowStartButton(): boolean {
    return !this.isLoading && !this.isCompleted && this.isRestartableStatus;
  }

  get isStartButtonDisabled(): boolean {
    return !this.canStart;
  }

  get isCompleted(): boolean {
    return this.taskStatus === 'COMPLETED';
  }

  get canShowLockedMessage(): boolean {
    return this.isDisabled && !this.isCompleted && this.isRestartableStatus;
  }

  private get isRestartableStatus(): boolean {
    return !this.taskStatus || this.taskStatus === 'CANCELLED';
  }

  onStartClicked(): void {
    if (!this.canStart) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.facade.startPracticeLevel(this.specializationId, LEVEL_ONE)
      .takeUntil(this.destroy$)
      .subscribe(
        task => {
          this.applyTask(task);
          this.isLoading = false;
          this.startPollingIfNeeded(task);
          this.cdr.markForCheck();
        },
        () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  private loadTaskStatus(): void {
    if (!this.specializationId) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.facade.getPracticeTaskStatus(this.specializationId, LEVEL_ONE)
      .takeUntil(this.destroy$)
      .subscribe(
        task => {
          this.applyTask(task);
          this.isLoading = false;
          this.startPollingIfNeeded(task);
          this.cdr.markForCheck();
        },
        () => {
          this.resetTask();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  private startPollingIfNeeded(task: SkillPracticeTaskResponse): void {
    if (task.status === 'CREATED' || task.status === 'IN_WORK') {
      this.startPolling();
    }
  }

  private startPolling(): void {
    this.clearPolling();
    this.pollingInterval = setInterval(() => {
      this.facade.getPracticeTaskStatus(this.specializationId, LEVEL_ONE)
        .takeUntil(this.destroy$)
        .subscribe(
          task => {
            this.applyTask(task);
            if (task.status === 'COMPLETED' || task.status === 'CANCELLED') {
              this.clearPolling();
              if (task.status === 'COMPLETED') {
                this.completed.emit();
              }
            }
            this.cdr.markForCheck();
          },
        );
    }, POLLING_INTERVAL_MS);
  }

  private clearPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private applyTask(task: SkillPracticeTaskResponse): void {
    this.taskStatus = task.status;
    this.curatorName = this.resolveCuratorName(task);
    this.curatorPhone = this.resolveCuratorPhone(task);
    this.planfixTaskId = task.planfixTaskId;
    this.planfixTaskUrl = task.planfixTaskUrl;
  }

  private resetTask(): void {
    this.taskStatus = null;
    this.curatorName = null;
    this.curatorPhone = null;
    this.planfixTaskId = null;
    this.planfixTaskUrl = null;
  }

  private resolveCuratorName(task: SkillPracticeTaskResponse): string | null {
    if (task.assignedCurator && task.assignedCurator.fullName) {
      return task.assignedCurator.fullName;
    }
    return null;
  }

  private resolveCuratorPhone(task: SkillPracticeTaskResponse): string | null {
    if (task.assignedCurator && task.assignedCurator.phone) {
      return task.assignedCurator.phone;
    }
    return null;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/level1-task-card/level1-task-card.component.ts